// Geometry for the market strip: where the frozen pool line, the consensus and
// every book's fair number sit on one axis.
//
// The panel's list answers "which books does our line beat"; it cannot show
// where the market has *piled up*, which is the question the whole app is
// about. Six books stacked at -4 while we hold -3 is a different picture from
// six books spread between -3 and -5, and a sorted column hides the difference.
//
// Geometry defaults to a 320-wide space. Wider panels supply their viewBox
// width to extend the axis without enlarging labels and markers. The layout
// remains independent of the DOM.

export const STRIP_WIDTH = 320;
// Wide enough for the team abbreviation that sits outside each end of the
// axis. At 14 a three-letter cap anchored to the plot edge ran off the
// viewBox and rendered as "AC" / "AR" — the SVG does not scroll, it clips.
export const STRIP_PAD = 30;
export const DOT_RADIUS = 4;
// Two dots closer than this share a column and stack instead of overlapping.
export const DOT_SPACING = 9;

const NFL_KEY_NUMBERS = [3, 7, 10, 14];

function niceStep(span) {
  if (span > 12) return 4;
  if (span > 8) return 2;
  if (span > 4) return 1;
  return 0.5;
}

/**
 * @param frozen  the frozen pool line, from the compared team's side
 * @param market  the de-vigged consensus, same side
 * @param books   rows from `rankBooks` — the de-vigged `fairLine` is what gets
 *                plotted, and `favour` (the same de-vigged comparison) is what
 *                shades it, so position and colour are one number.
 *
 * This strip is DESCRIPTIVE: it answers "where does each book really sit
 * against our number". That is a different question from "what is our line
 * worth", which is the card's job and is scored on posted numbers because
 * probability value is quantised to whole margins.
 *
 * Both orderings have been wrong here once. Plotting `fairLine` while shading
 * on posted-based line value put position and colour on different quantities —
 * ESPN BET de-vigging to -0.433 drew furthest past our -1.5 in grey while
 * LowVig at -0.98 drew mid-axis in green. Plotting posted numbers instead
 * fixed the colour but collapsed nine books onto four columns, throwing away
 * the thing the strip exists to show: books quote in half points, so their
 * posted numbers barely move while their prices say quite different things.
 * Shading on the same de-vigged number that places the dot satisfies both.
 */
export function stripLayout({ frozen = null, market = null, books = [], width = STRIP_WIDTH } = {}) {
  const dotValues = books
    .map((row) => row.fairLine)
    .filter((value) => Number.isFinite(value));
  const values = [frozen, market, ...dotValues].filter((value) => Number.isFinite(value));
  if (!values.length) return null;

  let lo = Math.min(...values);
  let hi = Math.max(...values);
  // A single point, or a tight cluster, still needs an axis with room to read.
  const pad = Math.max(0.75, (hi - lo) * 0.3);
  lo -= pad;
  hi += pad;
  const span = hi - lo;

  const x = (value) =>
    STRIP_PAD + ((value - lo) / span) * (width - STRIP_PAD * 2);

  const step = niceStep(span);
  const ticks = [];
  for (let value = Math.ceil(lo / step) * step; value <= hi; value += step) {
    // Guard the accumulated float: 0.5 steps drift enough to print "-3.0000001".
    const rounded = Math.round(value * 100) / 100;
    ticks.push({ value: rounded, x: x(rounded) });
  }

  // The margins that pay. Drawn faintly behind everything, because "our extra
  // half point crosses 3" is the reason a small gap outranks a big one.
  const keyLines = NFL_KEY_NUMBERS.flatMap((key) => [key, -key])
    .filter((value) => value > lo && value < hi)
    .map((value) => ({ value, x: x(value) }));

  // Stack collisions upward so a cluster reads as a stack rather than as one
  // fat dot — the clustering is the signal.
  const columns = [];
  const dots = books
    .filter((row) => Number.isFinite(row.fairLine))
    .map((row) => ({ row, x: x(row.fairLine) }))
    .sort((a, b) => a.x - b.x)
    .map((dot) => {
      let level = 0;
      while (columns[level] != null && dot.x - columns[level] < DOT_SPACING) level += 1;
      columns[level] = dot.x;
      return { ...dot, level };
    });

  return {
    domain: [lo, hi],
    x,
    ticks,
    keyLines,
    dots,
    levels: dots.reduce((max, dot) => Math.max(max, dot.level + 1), 1),
    frozenX: Number.isFinite(frozen) ? x(frozen) : null,
    marketX: Number.isFinite(market) ? x(market) : null,
  };
}
